import * as THREE from "three";
import { LollipopHelper } from "./lollipop-helper";
import { Button } from "./button";
import { EngineAudio } from "./EngineAudio";
import { AudioAssetMap } from "./AssetManager";
import { clamp, degToRad, lerp, MathUtils } from "three/src/math/MathUtils.js";
import { EasingFunction } from "./easing-utils";
import { PhysicalButton } from "./physical-button";
import * as ThreeMeshUI from "three-mesh-ui";

export enum UNICState {
  IDLE,
  LOOP_25,
  LOOP_100,
  TRANSITION_IDLE_TO_25,
  TRANSITION_25_TO_100,
  TRANSITION_100_TO_25,
  STOP_FROM_25,
  STOP_FROM_100,
  EMERGENCY_STOP,
  // STOP,
  // LOAD,
  // FUEL,
}

// about there
const ANIMATION_DURATIONS = {
  "engine-start-to-25-transition": 2.5,
  "engine-25-to-100-transition": 6,
  "engine-25-to-0-transition": 10,
  "engine-100-to-0-transition": 10,
  "engine-100-to-25-transition": 4,
  "engine-emergency-stop": 1.5,
}


/**
 * This is the physical control panel that the user can interact with, and which contains RPM, fuel and so
 */
export class UNICPanel extends THREE.Group {
  // private _buttons = [] as Button[]
  private _buttons = {
    startButton: new PhysicalButton(),
    stopButton: new PhysicalButton(),
    loadButton: new PhysicalButton(),
    fuelButton: new PhysicalButton(),
    emergencyStopButton: new PhysicalButton(),
  };

  private _unicState: UNICState = UNICState.IDLE;

  // Text support properties
  //   private _fontPath = "./Roboto-msdf.json";
  //   private _fontTexturePath = "./Roboto-msdf.png";
  private _fontPath = "./NotoSans-Regular-msdf.json";
  private _fontTexturePath = "./NotoSans-Regular.png";

  private _fontPathBold = "./NotoSans-Bold-msdf.json";
  private _fontTexturePathBold = "./NotoSans-Bold.png";


  get buttons(): Button[] {
    return Object.values(this._buttons);
  }

  private _engineAudio: EngineAudio;

  // these are the bigger 3D models, not on the panel itself
  private _fuelVariants: { dieselModel: THREE.Object3D, lngModel: THREE.Object3D, ammoniaModel: THREE.Object3D };

  private _fuelVariant: "diesel" | "lng" | "ammonia" = "diesel";

  private _buttonAudio: AudioBuffer;
  private _audioListener: THREE.AudioListener;

  private _indicatorArrow: THREE.Object3D;

  private _loadLabel: ThreeMeshUI.Block;
  private _fuelLabel: ThreeMeshUI.Block;
  private _modeLabel: ThreeMeshUI.Block;

  private _redColor = new THREE.Color("#ff1a00");
  private _greenColor = new THREE.Color("#1aff00");

  private _engineType: 'W25' | 'W31' = 'W25';

  get fuelVariant(): "diesel" | "lng" | "ammonia" {
    return this._fuelVariant;
  }

  get unicState(): UNICState {
    return this._unicState;
  }

  get emergencyStopButton(): PhysicalButton {
    return this._buttons.emergencyStopButton;
  }

  onFuelChange = (variant: "diesel" | "lng" | "ammonia") => { }
  onEmergencyStop = () => { }
  onButtonPressed = (button: PhysicalButton) => { }

  onSpeedChange = (newSpeed: number) => { }

  // arrow rotates 135 degrees both directions
  private _arrowRotation = {
    min: degToRad(135),
    max: degToRad(-135),
    start: 0,
    target: 0,
    animationTime: 1,
    animationPhase: 0,
    easing: EasingFunction.easeInOutCubic,
  }

  private setArrowTarget(target: number, time?: number): void {
    this._arrowRotation.start = this._arrowRotation.target;
    this._arrowRotation.target = target;
    this._arrowRotation.animationTime = time ?? 1;
    this._arrowRotation.animationPhase = 0;
  }

  constructor(
    engineAudio: EngineAudio,
    buttonMeshes: { startButton: THREE.Object3D; stopButton: THREE.Object3D; loadButton: THREE.Object3D; fuelButton: THREE.Object3D, emergencyStopButton: THREE.Object3D },
    fuelVariants: { dieselModel: THREE.Object3D; lngModel: THREE.Object3D; ammoniaModel: THREE.Object3D },
    buttonAudio: AudioBuffer,
    audioListener: THREE.AudioListener,
    indicatorArrow: THREE.Object3D,
    engineType: 'W25' | 'W31'
  ) {
    super();

    // this.add(new LollipopHelper(-10))
    // this.add(new THREE.AxesHelper(3))

    this._fuelVariants = {
      dieselModel: fuelVariants.dieselModel,
      lngModel: fuelVariants.lngModel,
      ammoniaModel: fuelVariants.ammoniaModel,
    };
    this._buttonAudio = buttonAudio;
    this._audioListener = audioListener;
    this._indicatorArrow = indicatorArrow;
    this._engineType = engineType;
    this._buttons.startButton.graphics = buttonMeshes.startButton;
    this._buttons.stopButton.graphics = buttonMeshes.stopButton;
    this._buttons.loadButton.graphics = buttonMeshes.loadButton;
    this._buttons.fuelButton.graphics = buttonMeshes.fuelButton;
    this._buttons.emergencyStopButton.graphics = buttonMeshes.emergencyStopButton;
    this._buttons.emergencyStopButton.offset = new THREE.Vector3(-0.015, 0, 0);
    // this._indicatorArrow.add(new THREE.AxesHelper());
    // this.add(new LollipopHelper());
    this.addButtons();
    this._engineAudio = engineAudio;
    this._engineAudio.onAudioPlayed = (
      audioKey: keyof typeof AudioAssetMap
    ) => {
      switch (audioKey) {
        case "engine-start-to-25-transition":
          this.onUNICStateRequest(UNICState.LOOP_25);
          break;
        case "engine-25-to-100-transition":
          this.onUNICStateRequest(UNICState.LOOP_100);
          break;
        case "engine-100-to-0-transition":
          this.onUNICStateRequest(UNICState.IDLE);
          break;
        case "engine-25-to-0-transition":
          this.onUNICStateRequest(UNICState.IDLE);
          break;
        case "engine-100-to-25-transition":
          this.onUNICStateRequest(UNICState.LOOP_25);
          break;
        case "engine-emergency-stop":
          this.onUNICStateRequest(UNICState.IDLE);
          break;
      }
    };


    this._loadLabel = this.createTextLabel("100", 0.015, true);
    this._loadLabel.position.x += 0.001
    this._loadLabel.position.z = 0.023
    this._loadLabel.position.y = 0.02

    this._fuelLabel = this.createTextLabel("Diesel", 0.004, true);
    this._fuelLabel.position.z = 0.023
    this._fuelLabel.position.y = -0.0415
    this.updateTextLabel(this._fuelLabel, "Diesel", this._greenColor);

    this._modeLabel = this.createTextLabel("Stop", 0.004, true);
    this._modeLabel.position.z = 0.050
    this._modeLabel.position.y = -0.05775
    this.updateTextLabel(this._modeLabel, "Stop", this._redColor);

    const operationModeLabel = this.createTextLabel("Operation Mode", 0.004, true);
    operationModeLabel.position.z = -0.004
    operationModeLabel.position.y = -0.05775
    this.updateTextLabel(operationModeLabel, "Normal", this._greenColor);


    this.setFuelVariant(this._fuelVariant);

    // this._loadLabel.position.copy(this._indicatorArrow.position);
    // this._loadLabel.add(new THREE.AxesHelper());
    this.add(this._loadLabel);
    this.add(this._fuelLabel);
    this.add(this._modeLabel);
    this.add(operationModeLabel);
    // indicatorArrow.visible = false;

  }

  private onUNICStateRequest(newState: UNICState): void {
    if (this._unicState === newState) {
      return;
    }
    if (this._unicState !== UNICState.IDLE && newState === UNICState.EMERGENCY_STOP) {
      this._engineAudio.playAudio("engine-emergency-stop");
      this._unicState = newState;
      this.updateTextLabel(this._modeLabel, "Stop", this._redColor);
      this.setArrowTarget(0, ANIMATION_DURATIONS["engine-emergency-stop"]);
      this.onEmergencyStop();
      return;
    }
    switch (this._unicState) {
      case UNICState.IDLE:
        if (newState === UNICState.TRANSITION_IDLE_TO_25) {
          this._engineAudio.playAudio("engine-start-to-25-transition");
          this._unicState = newState;
          this.setArrowTarget(0.25, ANIMATION_DURATIONS["engine-start-to-25-transition"]);
          this.updateTextLabel(this._modeLabel, "Run", this._greenColor);
        }
        break;
      case UNICState.TRANSITION_IDLE_TO_25:
        if (newState === UNICState.LOOP_25) {
          this._engineAudio.playAudio("engine-25-loop");
          this._unicState = newState;
        }
        break;
      case UNICState.LOOP_25:
        if (newState === UNICState.TRANSITION_25_TO_100) {
          this._engineAudio.playAudio("engine-25-to-100-transition");
          this._unicState = newState;
          this.setArrowTarget(1, ANIMATION_DURATIONS["engine-25-to-100-transition"]);
        }
        if (newState === UNICState.STOP_FROM_25) {
          this._engineAudio.playAudio("engine-25-to-0-transition");
          this._unicState = newState;
          this.updateTextLabel(this._modeLabel, "Stop", this._redColor);
          this.setArrowTarget(0, ANIMATION_DURATIONS["engine-25-to-0-transition"]);
        }
        break;
      case UNICState.TRANSITION_25_TO_100:
        if (newState === UNICState.LOOP_100) {
          this._engineAudio.playAudio("engine-100-loop");
          this._unicState = newState;
        }
        break;
      case UNICState.TRANSITION_100_TO_25:
        if (newState === UNICState.LOOP_25) {
          this._engineAudio.playAudio("engine-25-loop");
          this._unicState = newState;
        }
        break;
      case UNICState.LOOP_100:
        if (newState === UNICState.STOP_FROM_100) {
          this._engineAudio.playAudio("engine-100-to-0-transition");
          this._unicState = newState;
          this.updateTextLabel(this._modeLabel, "Stop", this._redColor);
          this.setArrowTarget(0, ANIMATION_DURATIONS["engine-100-to-0-transition"]);
        }
        if (newState === UNICState.TRANSITION_100_TO_25) {
          this._engineAudio.playAudio("engine-100-to-25-transition");
          this._unicState = newState;
          this.setArrowTarget(0.25, ANIMATION_DURATIONS["engine-100-to-25-transition"]);
        }
        break;
      case UNICState.STOP_FROM_25:
        if (newState === UNICState.IDLE) {
          this._engineAudio.stopAudio();
          this._unicState = newState;
          this.setArrowTarget(0, ANIMATION_DURATIONS["engine-25-to-0-transition"]);
        }
        break;
      case UNICState.STOP_FROM_100:
        if (newState === UNICState.IDLE) {
          this._engineAudio.stopAudio();
          this._unicState = newState;
          this.setArrowTarget(0, ANIMATION_DURATIONS["engine-100-to-0-transition"]);
        }
        break;
      case UNICState.EMERGENCY_STOP:
        if (newState === UNICState.IDLE) {
          this._engineAudio.stopAudio();
          this._unicState = newState;
        }
        break;
    }
    //   this._unicState = newState;
  }

  setFuelVariant(variant: "diesel" | "lng" | "ammonia"): void {
    if (this._fuelVariants === undefined || this._fuelVariants[variant as keyof typeof this._fuelVariants] === undefined) {
      console.log("Cannot set fuel variant label visibility", variant, this._fuelVariants)
      // return;
    } else {
      this._fuelVariants.ammoniaModel.visible = false;
      this._fuelVariants.lngModel.visible = false;
      this._fuelVariants.dieselModel.visible = false;
      this._fuelVariants[variant as keyof typeof this._fuelVariants].visible = true;
    }
    this._fuelVariant = variant;
    const labelText = variant === "diesel" ? "Diesel" : variant === "lng" ? "LNG" : "Ammonia";
    this.updateTextLabel(this._fuelLabel, labelText, this._greenColor);
    this.onFuelChange(variant);
  }

  setNextFuelVariant(): void {
    if (this._engineType === 'W31') {
      // W31 only has diesel and lng
      this._fuelVariant = this._fuelVariant === "diesel" ? "lng" : "diesel";
    } else {
      this._fuelVariant = this._fuelVariant === "diesel" ? "lng" : this._fuelVariant === "lng" ? "ammonia" : "diesel";
    }
    this.setFuelVariant(this._fuelVariant);
  }

  private _previousPercentageValue: number = 0;
  update(delta: number): void {
    let currentLoadValue = this._arrowRotation.target;

    if (this._indicatorArrow) {
      // console.log("indicator arrow", this._indicatorArrow);
      if (this._arrowRotation.animationTime > 0) {
        this._arrowRotation.animationPhase = Math.min(1, this._arrowRotation.animationPhase + delta / this._arrowRotation.animationTime);
        const eased = this._arrowRotation.easing(this._arrowRotation.animationPhase);
        // console.log("eased", eased);
        const intermediateSpot = lerp(this._arrowRotation.start, this._arrowRotation.target, eased);

        // this falls short of the target for 0.25
        // const eased = intermediate;
        this._indicatorArrow.rotation.x = lerp(this._arrowRotation.min, this._arrowRotation.max, intermediateSpot);
        currentLoadValue = intermediateSpot;
      } else {
        this._indicatorArrow.rotation.x = lerp(this._arrowRotation.min, this._arrowRotation.max, this._arrowRotation.target);
      }
    }

    // Update load label with percentage value
    const percentageValue = Math.round(currentLoadValue * 100);
    if (percentageValue !== this._previousPercentageValue) {
      this._previousPercentageValue = percentageValue;
      this.onSpeedChange(percentageValue);
    }
    this.updateTextLabel(this._loadLabel, percentageValue.toString());

    // Update ThreeMeshUI
    ThreeMeshUI.update();
  }

  /**
   * Create a text label using ThreeMeshUI
   * @param content - The text content to display
   * @param width - Width of the text container
   * @param height - Height of the text container
   * @returns ThreeMeshUI.Block containing the text
   */
  createTextLabel(content: string, fontSize: number = 0.01, bold: boolean = false): ThreeMeshUI.Block {
    const container = new ThreeMeshUI.Block({
      width: 1,
      height: 0.045,
      padding: 0.01,
      fontFamily: bold ? this._fontPathBold : this._fontPath,
      fontTexture: bold ? this._fontTexturePathBold : this._fontTexturePath,
      backgroundOpacity: 0,
      fontSize: fontSize,
    });
    // @ts-ignore This is actually a valid property
    container.borderRadius = 0.01;

    const text = new ThreeMeshUI.Text({
      content: content,
      opacity: 1,
      fontColor: new THREE.Color(0, 0, 0),
    });

    container.add(text);
    container.rotation.y = Math.PI / 2;
    container.position.x = -0.0097
    return container;
  }

  /**
   * Update the content of an existing text label
   * @param key - Identifier of the label to update
   * @param newContent - New text content
   */
  updateTextLabel(block: ThreeMeshUI.Block, newContent: string, color?: THREE.Color): void {
    if (block.children.length > 0) {
      const textElement = (block as any).childrenTexts[0] as any; // ThreeMeshUI.Text
      if (textElement && textElement.set) {
        const options: any = { content: newContent };
        if (color) {
          options.fontColor = color;
        }
        textElement.set(options);
        textElement.update();
      }
    }
  }


  /**
   * Add interactive buttons to the scene
   */
  private addButtons(): void {
    // Create a test button

    const buttonOffset = 0.034;

    // testBoxMesh.position.set(0, 1.715, 0);

    const startHeight = 0.0535;

    const sideOffset = -0.137;

    this.buttons.forEach((button) => {
      button.touchDistance = 0.015;
      button.scale.setScalar(0.15);
      button.position.set(-0.004, 0.0, sideOffset);
      button.addSelectSound(this._buttonAudio, this._audioListener);
    });

    this._buttons.startButton.position.y = startHeight; // closer to the user for development
    this.add(this._buttons.startButton);

    // we can request all we want, but the switch will determine if the request has any effect
    this._buttons.startButton.buttonAction = () => {
      this.onUNICStateRequest(UNICState.TRANSITION_IDLE_TO_25);
      this.onButtonPressed(this._buttons.startButton);
    };
    this._buttons.stopButton.position.y = startHeight - buttonOffset;
    this.add(this._buttons.stopButton);

    this._buttons.stopButton.buttonAction = () => {
      if (this._unicState === UNICState.LOOP_25) {
        this.onUNICStateRequest(UNICState.STOP_FROM_25);
      } else if (this._unicState === UNICState.LOOP_100) {
        this.onUNICStateRequest(UNICState.STOP_FROM_100);
      }
      this.onButtonPressed(this._buttons.stopButton);
    };


    this._buttons.fuelButton.position.y = startHeight - buttonOffset * 2;
    this.add(this._buttons.fuelButton);

    this._buttons.fuelButton.buttonAction = () => {
      this.setNextFuelVariant();
      console.log("fuel button pressed", this._fuelVariant);
      this.onButtonPressed(this._buttons.fuelButton);
    };

    this._buttons.loadButton.position.y = startHeight - buttonOffset * 3;
    this.add(this._buttons.loadButton);
    this._buttons.loadButton.buttonAction = () => {
      if (this._unicState === UNICState.LOOP_25) {
        this.onUNICStateRequest(UNICState.TRANSITION_25_TO_100);
      } else if (this._unicState === UNICState.LOOP_100) {
        this.onUNICStateRequest(UNICState.TRANSITION_100_TO_25);
      }
      this.onButtonPressed(this._buttons.loadButton);
    };

    this._buttons.emergencyStopButton.position.y = -0.19;
    this._buttons.emergencyStopButton.position.z = 0.15;
    this._buttons.emergencyStopButton.position.x += 0.02;
    this._buttons.emergencyStopButton.touchDistance = 0.03;
    this.add(this._buttons.emergencyStopButton);
    // this._buttons.emergencyStopButton.showDebug = true;
    this._buttons.emergencyStopButton.buttonAction = () => {
      this.onButtonPressed(this._buttons.emergencyStopButton);
      this.onUNICStateRequest(UNICState.EMERGENCY_STOP);
    };

    // console.log(`✅ Created test button(s)`);
  }

  printToLoadLabel(value: number): void {
    this.updateTextLabel(this._loadLabel, value.toString());
  }

}
